// ============================================
// moldClaw - Payment Hook (PortOne V2 SDK)
// ============================================

/**
 * 🧑‍💻 SWE-1: 결제 훅 (포트원 SDK 연동)
 * 🧑‍💻 SWE-2: 상태 관리
 * 🎨 CX-1: UX 피드백
 */

import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useAuth';

// 포트원 SDK 타입 (CDN 로드)
declare global {
  interface Window {
    PortOne?: {
      requestIssueBillingKey: (params: IssueBillingKeyParams) => Promise<IssueBillingKeyResponse>;
      requestPayment: (params: PaymentParams) => Promise<PaymentResponse>;
    };
  }
}

interface IssueBillingKeyParams {
  storeId: string;
  channelKey: string;
  billingKeyMethod: 'CARD';
  customer?: {
    customerId?: string;
    fullName?: string;
    email?: string;
    phoneNumber?: string;
  };
  windowType?: {
    pc?: 'IFRAME' | 'POPUP' | 'REDIRECTION';
    mobile?: 'IFRAME' | 'POPUP' | 'REDIRECTION';
  };
  redirectUrl?: string;
  customData?: Record<string, unknown>;
}

interface IssueBillingKeyResponse {
  code?: string;
  message?: string;
  billingKey?: string;
  card?: {
    number: string;
    issuer: string;
    brand: string;
    type: string;
  };
}

interface PaymentParams {
  storeId: string;
  paymentId: string;
  orderName: string;
  totalAmount: number;
  currency: 'KRW';
  payMethod: 'CARD';
  customer?: {
    customerId?: string;
    fullName?: string;
    email?: string;
  };
}

interface PaymentResponse {
  code?: string;
  message?: string;
  paymentId?: string;
  transactionId?: string;
}

// ============================================
// API Base URL & Mock Mode
// ============================================
const PAYMENT_API_URL = import.meta.env.VITE_PAYMENT_API_URL || 'http://localhost:8788';

// 💬 SWE-1: Mock 모드 - 포트원 API 키 없이 테스트 가능
const MOCK_MODE = import.meta.env.VITE_MOCK_PAYMENT === 'true';

// ============================================
// usePayment Hook
// ============================================

interface UsePaymentReturn {
  // 상태
  isLoading: boolean;
  error: string | null;
  billingKeys: BillingKey[];
  subscription: Subscription | null;
  paymentHistory: PaymentRecord[];
  
  // 액션
  loadBillingKeys: () => Promise<void>;
  loadSubscription: () => Promise<void>;
  loadPaymentHistory: () => Promise<void>;
  issueBillingKey: () => Promise<BillingKey | null>;
  deleteBillingKey: (id: string) => Promise<boolean>;
  setDefaultBillingKey: (id: string) => Promise<boolean>;
  subscribe: (tier: 'pro' | 'team', billingKeyId?: string) => Promise<boolean>;
  cancelSubscription: (reason?: string) => Promise<boolean>;
  
  // SDK 상태
  isSdkLoaded: boolean;
  
  // 💬 SWE-1: Mock 모드 상태
  isMockMode: boolean;
}

interface BillingKey {
  id: string;
  cardCompany: string | null;
  cardNumberMasked: string | null;
  cardType: string | null;
  isDefault: boolean;
  createdAt: string;
}

interface Subscription {
  id: string;
  tier: string;
  status: string;
  billingCycle: string;
  amount: number;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  nextBillingDate: string | null;
  cancelAtPeriodEnd: boolean;
  cancelledAt: string | null;
}

interface PaymentRecord {
  id: string;
  paymentId: string;
  amount: number;
  currency: string;
  productName: string;
  tier: string | null;
  status: string;
  receiptUrl: string | null;
  createdAt: string;
  paidAt: string | null;
}

// 포트원 설정 (런타임에 서버에서 가져옴)
let portoneConfig: { storeId: string; channelKey: string } | null = null;

export function usePayment(): UsePaymentReturn {
  const { user, session } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSdkLoaded, setIsSdkLoaded] = useState(false);
  const [billingKeys, setBillingKeys] = useState<BillingKey[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<PaymentRecord[]>([]);
  
  // ============================================
  // SDK 로드
  // ============================================
  
  useEffect(() => {
    // 포트원 SDK 스크립트 로드
    if (window.PortOne) {
      setIsSdkLoaded(true);
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://cdn.portone.io/v2/browser-sdk.js';
    script.async = true;
    script.onload = () => {
      setIsSdkLoaded(true);
      console.log('PortOne SDK loaded');
    };
    script.onerror = () => {
      console.error('Failed to load PortOne SDK');
    };
    document.head.appendChild(script);
    
    return () => {
      // 클린업 (옵셔널)
    };
  }, []);
  
  // 설정 로드
  useEffect(() => {
    if (portoneConfig) return;
    
    fetch(`${PAYMENT_API_URL}/api/v1/config`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          portoneConfig = data.data;
        }
      })
      .catch(err => console.error('Failed to load PortOne config:', err));
  }, []);
  
  // ============================================
  // API 호출 헬퍼
  // ============================================
  
  const apiCall = useCallback(async <T,>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> => {
    const token = session?.access_token;
    
    const response = await fetch(`${PAYMENT_API_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error?.message || '요청 처리 중 오류가 발생했습니다');
    }
    
    return data.data;
  }, [session]);
  
  // ============================================
  // 빌링키 관리
  // ============================================
  
  const loadBillingKeys = useCallback(async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      const data = await apiCall<{ billingKeys: any[] }>('/api/v1/billing-keys');
      
      setBillingKeys(data.billingKeys.map(bk => ({
        id: bk.id,
        cardCompany: bk.card_company,
        cardNumberMasked: bk.card_number_masked,
        cardType: bk.card_type,
        isDefault: bk.is_default,
        createdAt: bk.created_at,
      })));
    } catch (err) {
      console.error('Failed to load billing keys:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user, apiCall]);
  
  const issueBillingKey = useCallback(async (): Promise<BillingKey | null> => {
    if (!user) {
      setError('로그인이 필요합니다');
      return null;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // 💬 SWE-1: Mock 모드 처리
      if (MOCK_MODE) {
        console.log('[Mock Mode] 빌링키 발급 - 실제 포트원 SDK 호출 건너뜀');
        
        // Mock 엔드포인트 호출
        const data = await apiCall<{
          billingKeyId: string;
          card: {
            company: string;
            number: string;
            type: string;
          };
          isDefault: boolean;
          _mock: boolean;
        }>('/api/v1/mock/billing-keys', {
          method: 'POST',
          body: JSON.stringify({
            cardNumber: '4242424242424242',
            cardCompany: '신한카드',
            setAsDefault: true,
          }),
        });
        
        const newBillingKey: BillingKey = {
          id: data.billingKeyId,
          cardCompany: data.card.company,
          cardNumberMasked: data.card.number,
          cardType: data.card.type,
          isDefault: data.isDefault,
          createdAt: new Date().toISOString(),
        };
        
        setBillingKeys(prev => {
          if (newBillingKey.isDefault) {
            return [newBillingKey, ...prev.map(bk => ({ ...bk, isDefault: false }))];
          }
          return [newBillingKey, ...prev];
        });
        
        return newBillingKey;
      }
      
      // 실제 포트원 SDK 모드
      if (!window.PortOne || !portoneConfig) {
        setError('결제 SDK가 준비되지 않았습니다');
        return null;
      }
      
      // 1. 포트원 SDK로 빌링키 발급
      const result = await window.PortOne.requestIssueBillingKey({
        storeId: portoneConfig.storeId,
        channelKey: portoneConfig.channelKey,
        billingKeyMethod: 'CARD',
        customer: {
          customerId: user.id,
          email: user.email,
        },
        windowType: {
          pc: 'POPUP',
          mobile: 'POPUP',
        },
      });
      
      if (result.code) {
        // 사용자 취소
        if (result.code === 'USER_CANCEL') {
          return null;
        }
        throw new Error(result.message || '빌링키 발급 실패');
      }
      
      if (!result.billingKey) {
        throw new Error('빌링키를 받지 못했습니다');
      }
      
      // 2. 서버에 빌링키 등록
      const data = await apiCall<{
        billingKeyId: string;
        cardCompany: string;
        cardNumberMasked: string;
        isDefault: boolean;
      }>('/api/v1/billing-keys', {
        method: 'POST',
        body: JSON.stringify({
          billingKey: result.billingKey,
          cardCompany: result.card?.issuer,
          cardNumberMasked: result.card?.number,
          cardType: result.card?.type,
          setAsDefault: true,
        }),
      });
      
      const newBillingKey: BillingKey = {
        id: data.billingKeyId,
        cardCompany: data.cardCompany,
        cardNumberMasked: data.cardNumberMasked,
        cardType: result.card?.type || null,
        isDefault: data.isDefault,
        createdAt: new Date().toISOString(),
      };
      
      setBillingKeys(prev => {
        // 새 카드가 기본이면 기존 기본 해제
        if (newBillingKey.isDefault) {
          return [newBillingKey, ...prev.map(bk => ({ ...bk, isDefault: false }))];
        }
        return [newBillingKey, ...prev];
      });
      
      return newBillingKey;
      
    } catch (err) {
      const message = err instanceof Error ? err.message : '결제 수단 등록 실패';
      setError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [user, apiCall]);
  
  const deleteBillingKey = useCallback(async (id: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    
    try {
      await apiCall(`/api/v1/billing-keys/${id}`, { method: 'DELETE' });
      setBillingKeys(prev => prev.filter(bk => bk.id !== id));
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : '결제 수단 삭제 실패';
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [apiCall]);
  
  const setDefaultBillingKey = useCallback(async (id: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    
    try {
      await apiCall(`/api/v1/billing-keys/${id}/default`, { method: 'PUT' });
      setBillingKeys(prev => prev.map(bk => ({
        ...bk,
        isDefault: bk.id === id,
      })));
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : '기본 결제 수단 변경 실패';
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [apiCall]);
  
  // ============================================
  // 구독 관리
  // ============================================
  
  const loadSubscription = useCallback(async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      
      // 💬 SWE-1: Mock 모드 처리
      const endpoint = MOCK_MODE 
        ? '/api/v1/mock/subscriptions/current'
        : '/api/v1/payments/subscription';
      
      const data = await apiCall<any>(endpoint);
      
      // Mock 모드에서는 data 직접, 실제 모드에서는 data.subscription
      const sub = MOCK_MODE ? data : data.subscription;
      
      if (sub && sub.tier !== 'free' && sub.status !== 'none') {
        setSubscription({
          id: sub.id,
          tier: sub.tier,
          status: sub.status,
          billingCycle: sub.billingCycle || sub.billing_cycle,
          amount: sub.amount,
          currentPeriodStart: sub.currentPeriodStart || sub.current_period_start,
          currentPeriodEnd: sub.currentPeriodEnd || sub.current_period_end,
          nextBillingDate: sub.nextBillingDate || sub.next_billing_date,
          cancelAtPeriodEnd: sub.cancelAtPeriodEnd ?? sub.cancel_at_period_end,
          cancelledAt: sub.cancelledAt || sub.cancelled_at,
        });
      } else {
        setSubscription(null);
      }
    } catch (err) {
      console.error('Failed to load subscription:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user, apiCall]);
  
  const subscribe = useCallback(async (
    tier: 'pro' | 'team',
    billingKeyId?: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // 💬 SWE-1: Mock 모드 처리
      const endpoint = MOCK_MODE 
        ? '/api/v1/mock/payments'
        : '/api/v1/payments/subscribe';
      
      if (MOCK_MODE) {
        console.log('[Mock Mode] 구독 결제 - Mock 엔드포인트 사용');
      }
      
      await apiCall<{
        subscriptionId: string;
        tier: string;
        amount: number;
        currentPeriodEnd: string;
        receiptUrl?: string;
        _mock?: boolean;
      }>(endpoint, {
        method: 'POST',
        body: JSON.stringify({ tier, billingKeyId }),
      });
      
      // 구독 상태 업데이트
      await loadSubscription();
      
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : '구독 처리 실패';
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [apiCall, loadSubscription]);
  
  const cancelSubscription = useCallback(async (reason?: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // 💬 SWE-1: Mock 모드 처리
      const endpoint = MOCK_MODE 
        ? '/api/v1/mock/subscriptions/cancel'
        : '/api/v1/payments/cancel-subscription';
      
      if (MOCK_MODE) {
        console.log('[Mock Mode] 구독 취소 - Mock 엔드포인트 사용');
      }
      
      await apiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      
      // 구독 상태 업데이트
      await loadSubscription();
      
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : '구독 취소 실패';
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [apiCall, loadSubscription]);
  
  // ============================================
  // 결제 내역
  // ============================================
  
  const loadPaymentHistory = useCallback(async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      const data = await apiCall<{ payments: any[] }>('/api/v1/payments/history');
      
      setPaymentHistory(data.payments.map(p => ({
        id: p.id,
        paymentId: p.payment_id,
        amount: p.amount,
        currency: p.currency,
        productName: p.product_name,
        tier: p.tier,
        status: p.status,
        receiptUrl: p.receipt_url,
        createdAt: p.created_at,
        paidAt: p.paid_at,
      })));
    } catch (err) {
      console.error('Failed to load payment history:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user, apiCall]);
  
  return {
    isLoading,
    error,
    billingKeys,
    subscription,
    paymentHistory,
    loadBillingKeys,
    loadSubscription,
    loadPaymentHistory,
    issueBillingKey,
    deleteBillingKey,
    setDefaultBillingKey,
    subscribe,
    cancelSubscription,
    isSdkLoaded,
    isMockMode: MOCK_MODE,  // 💬 SWE-1: Mock 모드 노출
  };
}
